import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import MDEditor from "@uiw/react-md-editor";
import {
  deleteLesson,
  getLesson,
  updateLesson,
  type LessonDetail,
  type LessonType,
} from "../api/courses";
import { ApiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";

function canEditContent(roles: string[]): boolean {
  return roles.some((r) => r === "super_admin" || r === "admin" || r === "content_manager");
}

const LESSON_TYPES: { value: LessonType; label: string }[] = [
  { value: "text", label: "Теория" },
  { value: "video", label: "Видео" },
  { value: "file", label: "Файлы" },
  { value: "checklist", label: "Чек-лист" },
  { value: "quiz", label: "Тест" },
  { value: "mixed", label: "Смешанный" },
];

export function LessonEditPage() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const id = Number(lessonId);
  const navigate = useNavigate();
  const { user } = useCurrentUser();

  const [lesson, setLesson] = useState<LessonDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Editable fields
  const [title, setTitle] = useState("");
  const [lessonType, setLessonType] = useState<LessonType>("text");
  const [content, setContent] = useState<string | undefined>("");
  const [estimatedMinutes, setEstimatedMinutes] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [isRequired, setIsRequired] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"saved" | "dirty" | "error" | "idle">("idle");

  const load = useCallback(async () => {
    if (!Number.isFinite(id)) {
      setError("Некорректный урок");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const l = await getLesson(id);
      setLesson(l);
      setTitle(l.title);
      setLessonType(l.lesson_type);
      setContent(l.content_markdown ?? "");
      setEstimatedMinutes(l.estimated_minutes ? String(l.estimated_minutes) : "");
      setVideoUrl(l.video_url ?? "");
      setIsRequired(l.is_required_for_completion);
      setSaveStatus("saved");
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setError("Урок не найден");
      else setError(err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  function markDirty() {
    setSaveStatus("dirty");
  }

  async function handleSave() {
    if (!lesson) return;
    setSaving(true);
    setSaveStatus("dirty");
    try {
      const payload: Parameters<typeof updateLesson>[1] = {};
      if (title !== lesson.title) payload.title = title;
      if (lessonType !== lesson.lesson_type) payload.lesson_type = lessonType;
      if ((content ?? "") !== (lesson.content_markdown ?? "")) payload.content_markdown = content ?? "";
      if (videoUrl !== (lesson.video_url ?? "")) payload.video_url = videoUrl;
      const m = estimatedMinutes ? Number(estimatedMinutes) : null;
      if (m !== lesson.estimated_minutes && Number.isFinite(m as number)) {
        payload.estimated_minutes = m as number;
      }
      if (isRequired !== lesson.is_required_for_completion) payload.is_required_for_completion = isRequired;

      if (Object.keys(payload).length > 0) {
        await updateLesson(lesson.id, payload);
        await load();
      } else {
        setSaveStatus("saved");
      }
    } catch (err) {
      setSaveStatus("error");
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!lesson) return;
    if (!confirm(`Удалить урок «${lesson.title}»? Он будет помечен как архивный.`)) return;
    try {
      await deleteLesson(lesson.id);
      navigate(-1);
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    }
  }

  if (!user) return null;
  const canEdit = canEditContent(user.roles);

  if (loading && !lesson) {
    return <div className="mx-auto max-w-5xl px-4 py-12 text-center text-slate-400">Загрузка…</div>;
  }

  if (error || !lesson) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-12 text-center">
        <p className="text-slate-500">{error ?? "Урок не найден"}</p>
        <Link to="/courses" className="mt-4 inline-block text-sm text-brand-accent hover:underline">
          ← к курсам
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6" data-color-mode="light">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-sm text-slate-500 hover:text-brand-accent"
          >
            ← назад
          </button>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900">{lesson.title}</h1>
          <p className="mt-1 font-mono text-xs text-slate-500">{lesson.slug}</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <span
              className={`text-xs ${
                saveStatus === "saved"
                  ? "text-green-600"
                  : saveStatus === "dirty"
                    ? "text-amber-600"
                    : saveStatus === "error"
                      ? "text-red-600"
                      : "text-slate-400"
              }`}
            >
              {saveStatus === "saved" && "✓ сохранено"}
              {saveStatus === "dirty" && "не сохранено"}
              {saveStatus === "error" && "ошибка"}
            </span>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || saveStatus === "saved"}
              className="btn-primary"
            >
              {saving ? "Сохраняем…" : "Сохранить"}
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="btn-danger"
            >
              Удалить
            </button>
          </div>
        )}
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700">Название</label>
          <input
            type="text"
            value={title}
            disabled={!canEdit}
            onChange={(e) => {
              setTitle(e.target.value);
              markDirty();
            }}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700">Тип</label>
          <select
            value={lessonType}
            disabled={!canEdit}
            onChange={(e) => {
              setLessonType(e.target.value as LessonType);
              markDirty();
            }}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50"
          >
            {LESSON_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700">Время (мин.)</label>
          <input
            type="number"
            min={1}
            max={600}
            value={estimatedMinutes}
            disabled={!canEdit}
            onChange={(e) => {
              setEstimatedMinutes(e.target.value);
              markDirty();
            }}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700">Видео URL</label>
          <input
            type="url"
            placeholder="https://vk.com/video..."
            value={videoUrl}
            disabled={!canEdit}
            onChange={(e) => {
              setVideoUrl(e.target.value);
              markDirty();
            }}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50"
          />
        </div>
      </div>

      <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={isRequired}
          disabled={!canEdit}
          onChange={(e) => {
            setIsRequired(e.target.checked);
            markDirty();
          }}
        />
        Обязателен для завершения курса
      </label>

      <section className="mt-6">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Содержимое</h2>
          <p className="text-xs text-slate-500">
            Markdown.{" "}
            <a
              href="/docs/academy/CONTENT_FORMAT.md"
              target="_blank"
              rel="noreferrer"
              className="text-brand-accent hover:underline"
            >
              Правила вёрстки →
            </a>
          </p>
        </div>
        <div className="rounded-lg bg-white shadow-sm">
          <MDEditor
            value={content}
            onChange={(v) => {
              setContent(v ?? "");
              markDirty();
            }}
            height={500}
            preview={canEdit ? "live" : "preview"}
            visibleDragbar={false}
            textareaProps={{
              placeholder:
                "# Заголовок урока\n\nТекст. Используй :::important / :::tip / :::example / :::mistake / :::script для подсветки блоков.",
            }}
          />
        </div>
      </section>
    </div>
  );
}
